import React from 'react';
import { Box, Typography } from '@mui/material';
import { FeedsAnnouncement, Loader } from '@orijinworks/frontend-commons';
import { useFeedAnnouncementHook } from './useFeedAnnouncementHook';
import { useAnnouncementMenuHook } from '../../../hooks/useAnnouncementMenuHook';
import DeleteModal from '../../DeleteModal/DeleteModal';
import FeedMembersAndReceiptsInfoModal from '../../feed-members-and-receipts-info-modal';

/**
 * Component that displays a single announcement in a drawer
 * 
 * @param {Object} props - Component props
 * @param {Object} props.announcementData - Announcement data object to display (required)
 * @param {Function} [props.onAnnouncementDeleteHandler] - Optional callback function to execute after successful deletion
 * @returns {JSX.Element} The FeedAnnouncement component
 */
const FeedAnnouncement = React.memo(({ announcementData, onAnnouncementDeleteHandler }) => {
  const { announcement, error } = useFeedAnnouncementHook(announcementData);
  
  const {
    isLoading: isDeleteModalLoading,
    modalRef,
    getAnnouncementMenuOptions,
    isDeleteModalOpen,
    onContinueDeleteModalHandler,
    onCloseDeleteModalHandler,
    infoModalRef,
    isInfoModalLoading,
    infoModalData,
    infoModalProps
  } = useAnnouncementMenuHook(onAnnouncementDeleteHandler);

  // Show error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        padding="20px"
        role="alert"
        aria-label="Error loading announcement"
      >
        <Typography variant="body1" color="error">
          Failed to load announcement. Please try again.
        </Typography>
      </Box>
    );
  }

  // Show message if no announcement data (wait for data to load)
  // Don't proceed until we have a valid announcement with an id
  if (!announcement || !announcement.id) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        padding="20px"
        role="status"
        aria-label="Loading announcement"
      >
        <Typography variant="body1">
          Loading announcement...
        </Typography>
      </Box>
    );
  }

  // Only construct props and call getAnnouncementMenuOptions when announcement is fully ready
  // Map transformed announcement data to FeedsAnnouncement props
  // Note: Some props may need to be set to default values if not available in transformed data
  const menuOptions = getAnnouncementMenuOptions(announcement);
  
  // Disable Delete option if announcement is deleted
  const menuOptionsWithDisabledDelete = menuOptions.map(option => {
    if (option.text === 'Delete' && announcement.isDeleted) {
      return {
        ...option,
        isDisabled: true
      };
    }
    return option;
  });

  const announcementProps = {
    id: announcement.id,
    isNew: announcement.isNew || false,
    title: announcement.title || '',
    from: announcement.from || '',
    to: announcement.to || '',
    timestamp: announcement.timestamp || '',
    description: announcement.description || '',
    memberCount: announcement.memberCount || 0,
    announcementMenuOptions: menuOptionsWithDisabledDelete,
    showNoBorder: true,
  };

  return (
    <>
      <FeedsAnnouncement {...announcementProps} />
      
      <DeleteModal
        modalRef={modalRef}
        title="Delete Announcement"
        description="Are you sure you want to delete the selected announcement?"
        open={isDeleteModalOpen}
        onClose={onCloseDeleteModalHandler}
        onContinue={onContinueDeleteModalHandler}
      />
      
      <FeedMembersAndReceiptsInfoModal
        ref={infoModalRef}
        headingIcon={infoModalProps.headingIcon}
        heading={infoModalProps.heading}
        badgeLabel={infoModalData.badgeLabel}
        members={infoModalData.members}
        showFooter={false}
        styles={{
          mainWrapperStyles: {
            maxHeight: '450px',
            overflowY: 'auto'
          }
        }}
      />
      
      <Loader isLoading={isDeleteModalLoading || isInfoModalLoading} />
    </>
  );
});

FeedAnnouncement.displayName = 'FeedAnnouncement';

export default FeedAnnouncement;

